// TODO migrar a ActionMenu (@fiado/react-kit): reemplazar el DropdownMenu local + <Gate> por items
// declarativos con permission/scope/scopeRef. Scope TENANT/tenantId (no PLATFORM) → pasar scopeRef.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { platformApi } from '../../../../api/endpoints/platform';
import { handleApiError } from '../../../../api/errorMapping';
import { useAuth } from '../../../../hooks/useAuth';
import { Gate } from '../../../../components/permissions/Gate';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../../../../components/ui/dropdown-menu';
import { Button } from '../../../../components/ui/button';
import { RolePermissionsView } from '../../../../components/tenant/RolePermissionsView';
import { PlatformTenantRoleForm } from './PlatformTenantRoleForm';
import type { LabelResolver } from '../../../../hooks/useLabelResolver';
import type { TenantRoleListItemLocal } from '../../../../api/types/TenantLocal';
import type { Permission } from '../../../../api/types/permissions';

// Permisos que habilitan alguna acción del menú. Si el user no tiene ninguno (ni scope sobre el
// tenant del path), no renderizamos el "⋮". Cada item igual se gatea con <Gate>. Son los permisos
// `tenant.role.*` del catálogo (el platform admin los tiene y alcanza el scope TENANT por rank).
const ACTION_PERMS: Permission[] = ['tenant.role.update', 'tenant.role.delete'];

interface Props {
    role: TenantRoleListItemLocal;
    /** tenantId del DRILL-DOWN (param de ruta), NO el del user logueado. */
    tenantId: string;
    /** Rol de sistema: ver detalle y clonar; no se edita ni se borra. */
    isSystem: boolean;
    /** Etiquetas del tenant visto (catálogo, niveles y nombre). */
    labels: LabelResolver;
}

/** Menú «⋮» de un rol del tenant visto: ver detalle, clonar (sistema) o editar y eliminar (personalizado). */
export function PlatformTenantRoleRowActions({ role, tenantId, isSystem, labels }: Props) {
    const qc = useQueryClient();
    const { hasPermission, hasScopeAccess } = useAuth();
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [cloneOpen, setCloneOpen] = useState(false);
    // Editar y eliminar solo aplican a roles personalizados, con permiso y alcance sobre el tenant.
    const canManage =
        !isSystem &&
        hasScopeAccess('TENANT', tenantId) &&
        ACTION_PERMS.some((p) => hasPermission(p));
    const permissions = role.permissions ?? [];

    const invalidate = (): Promise<void> =>
        qc.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId, 'roles'] });

    const deleteMut = useMutation({
        mutationFn: () => platformApi.deleteTenantRole(tenantId, role.roleId),
        onSuccess: async () => {
            toast.success('Rol eliminado');
            setConfirmDelete(false);
            await invalidate();
        },
        onError: handleApiError,
    });

    return (
        <>
            {/* modal={false}: evita el page-lock de Radix (pointer-events:none en body) cuando la
                fila se desmonta tras confirmar una acción. */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="Acciones">
                        <MoreVertical className="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                        onClick={() => {
                            setDetailOpen(true);
                        }}
                    >
                        Ver detalle
                    </DropdownMenuItem>
                    {isSystem && (
                        <Gate permission="tenant.role.create" scope="TENANT" scopeRef={tenantId}>
                            <DropdownMenuItem
                                onClick={() => {
                                    setCloneOpen(true);
                                }}
                            >
                                Clonar a personalizado
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {canManage && (
                        <>
                            <DropdownMenuSeparator />
                            <Gate
                                permission="tenant.role.update"
                                scope="TENANT"
                                scopeRef={tenantId}
                            >
                                <DropdownMenuItem
                                    onClick={() => {
                                        setEditOpen(true);
                                    }}
                                >
                                    Editar
                                </DropdownMenuItem>
                            </Gate>
                            <Gate
                                permission="tenant.role.delete"
                                scope="TENANT"
                                scopeRef={tenantId}
                            >
                                <DropdownMenuItem
                                    onClick={() => {
                                        setConfirmDelete(true);
                                    }}
                                >
                                    Eliminar
                                </DropdownMenuItem>
                            </Gate>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Ver detalle: modal de solo lectura con los permisos del rol. */}
            {detailOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setDetailOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold">{role.roleName}</h2>
                                {role.description && (
                                    <p className="text-sm text-muted-foreground">
                                        {role.description}
                                    </p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                    {isSystem ? 'Rol de sistema' : 'Rol personalizado'} ·{' '}
                                    {String(permissions.length)} permiso(s)
                                </p>
                            </div>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setDetailOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            <RolePermissionsView permissions={permissions} labels={labels} />
                        </div>
                    </div>
                </div>
            )}

            {/* Clonar: alta de un rol personalizado con el nombre y los permisos del de sistema. */}
            {cloneOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCloneOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                Clonar {role.roleName} a personalizado
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCloneOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformTenantRoleForm
                            tenantId={tenantId}
                            initialName={`Copia de ${role.roleName}`}
                            initialPermissions={permissions}
                            onDone={() => {
                                setCloneOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Eliminar rol"
                description={`El rol "${role.roleName}" se eliminará. Los usuarios que lo tengan asignado perderán esos permisos. ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                onConfirm={() => {
                    deleteMut.mutate();
                }}
            />

            {/* Editar: modal plano (mismo patrón que la tabla de crear). */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setEditOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Editar rol</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setEditOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformTenantRoleForm
                            tenantId={tenantId}
                            role={role}
                            onDone={() => {
                                setEditOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
